/**
 * Rung 2: a statement about the country, true or false (change 5).
 *
 * THIS RUNG IS SUPPOSED TO FEEL FASTER THAN THE OTHERS. It is the accessible
 * one — the rung a player who is not sure of themselves can still clear, which
 * is the whole reason it exists (see MVP-PLAN §2 on why its EV is what it is).
 * So it gets two targets instead of four, each of them enormous, each with its
 * own colour and its own word, and no reading order to work out: TRUE is always
 * left, FALSE is always right, in every round of every match.
 *
 * That fixity is deliberate and is NOT the shuffle-order rule being broken. The
 * server still decides which OPTION INDEX is true — `options[k].id` is
 * `TRUE_OPTION_ID` or `FALSE_OPTION_ID` — and this component reads the ids to
 * find them. What is fixed is where the words sit on screen, which carries no
 * information about the answer and saves the player half a second of hunting
 * they can spend thinking instead.
 *
 * Hit targets are 96px tall against the 44px minimum, because at 375px wide two
 * of them fill the thumb's whole arc and a mis-tap here costs the round.
 */

import { FALSE_OPTION_ID, TRUE_OPTION_ID } from '@geogame/shared';
import type { RoundPayloadOption } from '@geogame/shared';

export interface TrueFalseProbeProps {
  readonly options: readonly [RoundPayloadOption, RoundPayloadOption];
  readonly disabled: boolean;
  /** Index already sent, so the pressed side stays pressed while it flies. */
  readonly chosenIdx: number | null;
  readonly onChoose: (idx: number) => void;
}

/** Which option index carries which verdict. The server chose; we only read. */
export function verdictIndices(options: readonly RoundPayloadOption[]): {
  trueIdx: number;
  falseIdx: number;
} {
  const trueIdx = options.findIndex((o) => o.id === TRUE_OPTION_ID);
  const falseIdx = options.findIndex((o) => o.id === FALSE_OPTION_ID);
  // A payload that named neither is a malformed rung, not a guess to make: fall
  // back to positional order so the player still has two buttons to press.
  return {
    trueIdx: trueIdx >= 0 ? trueIdx : 0,
    falseIdx: falseIdx >= 0 ? falseIdx : 1,
  };
}

export function TrueFalseProbe({
  options,
  disabled,
  chosenIdx,
  onChoose,
}: TrueFalseProbeProps): React.JSX.Element {
  const { trueIdx, falseIdx } = verdictIndices(options);

  return (
    <div className="gg-tf">
      <button
        type="button"
        className="gg-tf-btn"
        data-verdict="true"
        data-chosen={chosenIdx === trueIdx ? 'true' : 'false'}
        disabled={disabled}
        onClick={() => onChoose(trueIdx)}
      >
        <span className="gg-tf-mark" aria-hidden="true">
          ✓
        </span>
        <span className="gg-tf-word">True</span>
      </button>
      <button
        type="button"
        className="gg-tf-btn"
        data-verdict="false"
        data-chosen={chosenIdx === falseIdx ? 'true' : 'false'}
        disabled={disabled}
        onClick={() => onChoose(falseIdx)}
      >
        <span className="gg-tf-mark" aria-hidden="true">
          ✕
        </span>
        <span className="gg-tf-word">False</span>
      </button>
    </div>
  );
}
